/**
 * Analytics abstraction for NextOpp.io.
 *
 * Thin, typed wrapper over PostHog (loaded in public/index.html). If PostHog is
 * not present (local dev without a key, tests, SSR), every call becomes a no-op
 * so product code never has to null-check `window.posthog`.
 *
 * Event names + properties are the canonical taxonomy documented in
 * docs/ANALYTICS_EVENTS.md. Import the named helpers rather than passing raw
 * strings, so the taxonomy stays consistent.
 *
 * Toggle off explicitly with REACT_APP_ANALYTICS_DISABLED=true.
 */

const DISABLED =
  typeof process !== "undefined" &&
  process.env &&
  process.env.REACT_APP_ANALYTICS_DISABLED === "true";

function ph() {
  if (DISABLED) return null;
  if (typeof window === "undefined") return null;
  return window.posthog || null;
}

/** Canonical event names — keep in sync with docs/ANALYTICS_EVENTS.md. */
export const EVENTS = {
  PAGE_VIEW: "page_view",
  SIGNUP: "signup",
  LOGIN: "login",
  ONBOARDING_COMPLETED: "onboarding_completed",
  RESUME_UPLOADED: "resume_uploaded",
  RESUME_PARSED: "resume_parsed",
  JOB_ADDED: "job_added",
  ATS_ANALYSIS_GENERATED: "ats_analysis_generated",
  RESUME_TAILORED: "resume_tailored",
  COVER_LETTER_GENERATED: "cover_letter_generated",
  INTERVIEW_PREP_GENERATED: "interview_prep_generated",
  APPLICATION_STATUS_CHANGED: "application_status_changed",
  FOLLOWUP_SET: "followup_set",
  UPGRADE_CLICKED: "upgrade_clicked",
  CHECKOUT_STARTED: "checkout_started",
  SUBSCRIPTION_ACTIVATED: "subscription_activated",
  FEEDBACK_SUBMITTED: "feedback_submitted",
};

/** Low-level capture. Prefer the named helpers below. */
export function track(event, properties = {}) {
  const client = ph();
  if (!client || typeof client.capture !== "function") return;
  try {
    client.capture(event, properties);
  } catch (_) {
    /* never let analytics break the app */
  }
}

/** Associate the current session with a signed-in user. */
export function identify(userId, traits = {}) {
  const client = ph();
  if (!client || typeof client.identify !== "function") return;
  try {
    client.identify(userId, traits);
  } catch (_) {
    /* no-op */
  }
}

/** Clear identity on logout. */
export function reset() {
  const client = ph();
  if (!client || typeof client.reset !== "function") return;
  try {
    client.reset();
  } catch (_) {
    /* no-op */
  }
}

/** Route/page view. Call from a router effect. */
export function page(path, properties = {}) {
  track(EVENTS.PAGE_VIEW, { path, ...properties });
}

// ---- Named helpers (activation / monetization / retention funnels) ----
export const analytics = {
  signup: (method) => track(EVENTS.SIGNUP, { method }),
  login: (method) => track(EVENTS.LOGIN, { method }),
  onboardingCompleted: (props) => track(EVENTS.ONBOARDING_COMPLETED, props),
  resumeUploaded: (props) => track(EVENTS.RESUME_UPLOADED, props),
  resumeParsed: (props) => track(EVENTS.RESUME_PARSED, props),
  jobAdded: (source) => track(EVENTS.JOB_ADDED, { source }),
  atsAnalysisGenerated: (props) => track(EVENTS.ATS_ANALYSIS_GENERATED, props),
  resumeTailored: (props) => track(EVENTS.RESUME_TAILORED, props),
  coverLetterGenerated: (tone) =>
    track(EVENTS.COVER_LETTER_GENERATED, { tone }),
  interviewPrepGenerated: (props) =>
    track(EVENTS.INTERVIEW_PREP_GENERATED, props),
  applicationStatusChanged: (from, to) =>
    track(EVENTS.APPLICATION_STATUS_CHANGED, { from, to }),
  followupSet: (props) => track(EVENTS.FOLLOWUP_SET, props),
  upgradeClicked: (tier, location) =>
    track(EVENTS.UPGRADE_CLICKED, { tier, location }),
  checkoutStarted: (tier) => track(EVENTS.CHECKOUT_STARTED, { tier }),
  subscriptionActivated: (tier) =>
    track(EVENTS.SUBSCRIPTION_ACTIVATED, { tier }),
  feedbackSubmitted: (category) =>
    track(EVENTS.FEEDBACK_SUBMITTED, { category }),
};

export default analytics;
